import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogIn, LogOut, MessageCircle, Gift, Users, Download, ChevronRight, Lock, Wallet, User as UserIcon, Sun, Moon } from "lucide-react";
import { BottomNav } from "@/components/BottomNav";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { PhoneVerificationBanner } from "@/components/PhoneVerificationBanner";
import { usePhoneVerification } from "@/hooks/usePhoneVerification";
import { analyticsService } from "@/services/analytics";
import { isMobileApp } from "@/utils/browser";
import { useCoreController } from "@/services/coreController";
import { parrainageService } from "@/services/parrainageService";
import directus from "@/lib/directus";
import { readItems } from "@directus/sdk";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

function Row({
  icon: Icon,
  label,
  hint,
  to,
  href,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint?: string;
  to?: string;
  href?: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <span className="grid size-9 place-items-center rounded-lg bg-accent-brand-soft">
        <Icon className="size-4 text-accent-brand" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="mt-0.5 text-[11px] text-text-subtle">{hint}</p>}
      </div>
      <ChevronRight className="size-4 text-foreground/30" />
    </>
  );
  const cls =
    "flex w-full items-center gap-3 rounded-xl bg-card-muted px-3.5 py-3 text-left ring-1 ring-card-muted-ring transition hover:bg-muted";
  if (to) return <Link to={to} className={cls}>{content}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls}>{content}</a>;
  return <button onClick={onClick} className={cls}>{content}</button>;
}

function AccountPage() {
  const { user, isAuthenticated, signOutUser } = useAuth();
  const navigate = useNavigate();
  const coreController = useCoreController();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  const { needsVerification, checkStatus, markVerified, dismissPrompt } = usePhoneVerification();
  const [referralStats, setReferralStats] = useState({ count: 0, deposits: 0, earnings: 0 });

  const commercialPhone = user?.phone_number || coreController.getCommercialId();

  useEffect(() => {
    analyticsService.trackScreenView("account_page");
  }, []);

  useEffect(() => {
    const loadReferralStats = async () => {
      if (!commercialPhone) return;
      try {
        const [parrainagesResult, relationsResult] = await Promise.all([
          parrainageService.fetchParrainageList(commercialPhone),
          directus.request(
            readItems("commercial_relations", {
              filter: {
                _or: [
                  { commercial_phoneNumber: { _eq: commercialPhone } },
                  { user_id: { id: { _eq: user?.id ?? "" } } },
                ],
              },
              fields: ["balance", "commission", "commercial_phoneNumber"],
              limit: 1,
            })
          ),
        ]);
        const parrainages = (parrainagesResult?.parrainages as any[]) ?? [];
        const relation = Array.isArray(relationsResult) && relationsResult.length > 0 ? relationsResult[0] : null;
        setReferralStats({
          count: parrainages.length,
          deposits: 0,
          earnings: relation?.balance ?? 0,
        });
      } catch (error) {
        console.error("Error loading referral stats:", error);
      }
    };
    loadReferralStats();
  }, [commercialPhone, user?.id]);

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const u = user as any;
  const displayName = u?.displayName || (u ? [u.firstName, u.lastName].filter(Boolean).join(" ") : null);
  const initials = displayName
    ? displayName.split(" ").map((n: string) => n[0]).slice(0, 2).join("").toUpperCase()
    : "INV";

  return (
    <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)",
        }}
      />
      <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
          <Link to="/" aria-label="Retour" className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring">
            <ArrowLeft className="size-4" />
          </Link>
          <h1 className="text-base font-semibold tracking-tight">Mon compte</h1>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[52ch] flex-1 space-y-4 px-4 py-4 lg:max-w-6xl lg:px-8">
        {/* Statut */}
        <section className="rounded-2xl bg-card-muted p-4 ring-1 ring-card-muted-ring">
          <div className="flex items-center gap-3">
            <div className="grid size-12 place-items-center rounded-full bg-muted text-lg font-bold ring-1 ring-card-muted-ring">
              {isAuthenticated ? initials : <UserIcon className="size-5" />}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-accent-brand">Statut</p>
              <p className="mt-0.5 truncate text-lg font-semibold">
                {isAuthenticated ? displayName || "Utilisateur" : "Invité"}
              </p>
            </div>
          </div>
          {isAuthenticated ? (
            <>
              <p className="mt-3 text-xs text-muted-foreground">
                {user?.email ? `${user.email}` : ""} — votre historique et parrainage sont synchronisés.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-muted px-4 py-2.5 text-sm font-semibold text-foreground ring-1 ring-card-muted-ring transition active:scale-95"
                >
                  <LogOut className="size-4" /> Déconnexion
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-3 text-xs text-muted-foreground">Connecte-toi pour sauvegarder ton historique et parrainer tes amis.</p>
              <button
                onClick={() => navigate("/login")}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-accent-brand py-2.5 text-sm font-semibold text-white"
              >
                <LogIn className="size-4" /> Se connecter
              </button>
              <button
                onClick={() => navigate("/register")}
                className="mt-2 w-full text-center text-[11px] text-text-subtle hover:text-foreground"
              >
                Pas encore de compte ? Créer un compte
              </button>
            </>
          )}
        </section>

        {/* Parrainage */}
        <section
          className="relative overflow-hidden rounded-2xl p-4 ring-1 ring-black/10"
          style={{ background: "linear-gradient(135deg, #002366 0%, #0052cc 100%)" }}
        >
          <div className="flex items-start gap-3">
            <span
              className="grid size-11 shrink-0 place-items-center rounded-2xl bg-white/25"
            >
              <Users className="size-5 text-white" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/90">Parrainage</p>
              <p className="mt-0.5 text-base font-semibold leading-tight text-white">Gagne 250 FCFA par ami</p>
              <p className="mt-1 text-[12px] leading-relaxed text-white/90">
                Invite tes amis avec ton lien unique. À chaque recharge d'un filleul, tu touches <b className="text-white">250 FCFA</b> — sans limite.
              </p>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-white/15 p-2.5 ring-1 ring-white/20">
              <Users className="size-3.5 text-white" />
              <p className="mt-1 text-[10px] uppercase tracking-wide text-white/90">Filleuls</p>
              <p className="text-sm font-semibold text-white">{isAuthenticated ? (referralStats.count || 0) : (referralStats.count || "—")}</p>
            </div>
            <div className="rounded-xl bg-white/15 p-2.5 ring-1 ring-white/20">
              <Wallet className="size-3.5 text-white" />
              <p className="mt-1 text-[10px] uppercase tracking-wide text-white/90">Gains</p>
              <p className="text-sm font-semibold text-white">{isAuthenticated ? (referralStats.earnings || 0).toLocaleString("fr-FR") : (referralStats.earnings ? referralStats.earnings.toLocaleString("fr-FR") : "—")}</p>
            </div>
          </div>

          {isAuthenticated || commercialPhone ? (
            <button
              onClick={() => navigate("/parrainage")}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-card py-2.5 text-sm font-semibold text-foreground ring-1 ring-border"
            >
              <Users className="size-4" /> Voir mon parrainage
            </button>
          ) : (
            <>
              <div className="mt-3 flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2.5 ring-1 ring-white/10">
                <Lock className="size-3.5 text-white/80" />
                <p className="min-w-0 flex-1 text-[11px] leading-snug text-white/80">
                  Crée ton compte pour obtenir ton lien de parrainage, suivre tes filleuls et être payé à chaque recharge.
                </p>
              </div>
              <button
                onClick={() => navigate("/register")}
                className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl bg-card py-2.5 text-sm font-semibold text-foreground ring-1 ring-border"
              >
                <LogIn className="size-4" /> Créer mon compte
              </button>
            </>
          )}
        </section>

        {/* Application */}
        <section className="space-y-2">
          <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-accent-brand">Application</p>
          {isAuthenticated && needsVerification && (
            <button
              onClick={() => setShowPhoneVerify(true)}
              className="flex w-full items-center gap-3 rounded-xl bg-gradient-to-r from-green-500/10 to-blue-500/10 px-3.5 py-3 text-left ring-1 ring-green-500/20 transition hover:bg-muted"
            >
              <span className="grid size-9 place-items-center rounded-lg bg-green-500/20">
                <Phone className="size-4 text-green-600" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">Valider mon numéro de téléphone</p>
                <p className="mt-0.5 text-[11px] text-text-subtle">Sécurise ton compte et reçois les alertes WhatsApp</p>
              </div>
              <ChevronRight className="size-4 text-foreground/30" />
            </button>
          )}
          {!isMobileApp() && (
            <Row icon={Download} label="Installer l'application" hint="APK Android ou ajouter à l'écran d'accueil" to="/install" />
          )}
          <Row icon={Gift} label="Coupons de recharge" hint="Utiliser un code de recharge" to="/recharge-coupon" />
        </section>

        {/* Aide */}
        <section className="space-y-2">
          <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-accent-brand">Aide</p>
          <Row
            icon={MessageCircle}
            label="Contacter le support"
            hint="Réponse rapide via WhatsApp"
            href="https://wa.me/22605756806"
          />
        </section>

        {/* Apparence */}
        <section className="space-y-2">
          <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-accent-brand">Apparence</p>
          <ThemeToggleRow />
        </section>

        <p className="pt-2 text-center text-[10px] text-text-subtle">EASYSERVICE EXPRESS · v1.0</p>
      </main>

      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="max-w-[90vw] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-center">Se déconnecter ?</DialogTitle>
            <DialogDescription className="text-center">
              Vous devrez vous reconnecter pour accéder à vos transactions et votre parrainage.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setShowLogoutConfirm(false)}
              className="flex-1 rounded-xl"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                setShowLogoutConfirm(false);
                await handleLogout();
              }}
              className="flex-1 rounded-xl"
            >
              Se déconnecter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhoneVerificationBanner
        open={showPhoneVerify}
        onDismiss={() => {
          setShowPhoneVerify(false)
          dismissPrompt()
        }}
        onVerified={(phone) => {
          markVerified(phone, true)
          checkStatus()
          setShowPhoneVerify(false)
        }}
      />

      <BottomNav />
    </div>
  );
}

function ThemeToggleRow() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      onClick={toggleTheme}
      className="flex w-full items-center gap-3 rounded-xl bg-card-muted p-3 text-left ring-1 ring-card-muted-ring transition hover:bg-card-muted-3"
    >
      <span className="grid size-9 place-items-center rounded-lg bg-muted">
        {isDark ? <Moon className="size-4 text-foreground" /> : <Sun className="size-4 text-foreground" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">Thème</p>
        <p className="mt-0.5 text-[11px] text-text-subtle">
          {isDark ? "Mode sombre actif" : "Mode clair actif"}
        </p>
      </div>
      <ChevronRight className="size-4 text-text-subtle" />
    </button>
  );
}

export default AccountPage;